'use client';
import { useEffect, useState } from 'react';
import { useSearchParams, useRouter, usePathname } from 'next/navigation';
import {
  GEAR,
  FORMAT,
  GEAR_DISPLAY_ORDER,
  isGearFormatCompatible,
} from '@/utils/constants';
import { useScrollFade } from '@/hooks/useScrollFade';
import { useTranslations } from 'next-intl';
import { GearChip } from '@/components/ui/GearChip/GearChip';
import { orderSearchParams } from '@/utils/search/params';

const FilterIcon = ({ size = 20 }: { size?: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    aria-hidden
  >
    <path d="M3 6.00012H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 12.0001H17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M10 18.0001H14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Chip row uses the canonical gear order from constants. Labels come from
// GEAR_TITLE_MAP so they don't drift.
const CHIP_ORDER = GEAR_DISPLAY_ORDER;

interface GearChipsProps {
  superPlatform?: FORMAT;
  selectGears?: GEAR[];
  hideGearFilter?: boolean;
  hasUserAppliedFilters: boolean;
  onOpenMobileFilters: () => void;
}

const GearChips = ({ superPlatform, selectGears, hideGearFilter, hasUserAppliedFilters, onOpenMobileFilters }: GearChipsProps) => {
  const searchParams = useSearchParams();
  const router = useRouter();
  const pathname = usePathname();
  const t = useTranslations('search');
  const urlGears = (searchParams.get('gears')?.split('_') as GEAR[]) || [];
  const [selectedGears, setSelectedGears] = useState<GEAR[]>(urlGears);
  const [isMobile, setIsMobile] = useState(false);
  const fade = useScrollFade([superPlatform, isMobile]);

  useEffect(() => {
    if (urlGears.toString() === selectedGears.toString()) return;
    setSelectedGears(urlGears);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlGears.toString()]);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 767px)');
    const update = () => setIsMobile(mql.matches);
    update();
    mql.addEventListener('change', update);
    return () => mql.removeEventListener('change', update);
  }, []);

  if (hideGearFilter) return null;

  // Gear is meaningful for every Format. When a Format is active (search
  // filter or OAuth/embed lock), incompatible Gears are shown but disabled
  // rather than removed (NAM disables Spaces; IR disables Amp/Amp + Cab).
  const isLocked = !!selectGears;
  // The authorize endpoint canonicalizes gears on persist, so a locked set only
  // ever contains catalog gears that appear in CHIP_ORDER.
  const activeSet = new Set(isLocked ? selectGears : selectedGears);
  const isFormatIncompatible = (g: GEAR) =>
    !!superPlatform && !isGearFormatCompatible(g, superPlatform as FORMAT);
  const isChipDisabled = (g: GEAR) => (isLocked && !activeSet.has(g)) || isFormatIncompatible(g);

  // Active chips first on mobile, then disabled chips sink to the end.
  const baseOrder = isMobile
    ? [...CHIP_ORDER.filter((g) => activeSet.has(g)), ...CHIP_ORDER.filter((g) => !activeSet.has(g))]
    : CHIP_ORDER;
  const orderedChips = [
    ...baseOrder.filter((g) => !isChipDisabled(g)),
    ...baseOrder.filter((g) => isChipDisabled(g)),
  ];

  const onToggle = (gear: GEAR) => {
    if (isLocked || isFormatIncompatible(gear)) return;
    const isActive = selectedGears.includes(gear);
    const next = isActive ? selectedGears.filter((g) => g !== gear) : [...selectedGears, gear];
    const newParams = new URLSearchParams(searchParams);
    if (next.length === 0 || next.length === CHIP_ORDER.length) {
      newParams.delete('gears');
    } else {
      newParams.set('gears', next.join('_'));
    }
    newParams.delete('page');
    setSelectedGears(next);
    fade.ref.current?.scrollTo({ left: 0, behavior: 'smooth' });
    router.push(`${pathname}?${orderSearchParams(newParams)}`, { scroll: false });
  };

  // Always fades the left edge to tuck chips under the filter button; the
  // right edge fades only while there's more to scroll.
  const mobileMask = fade.atEnd
    ? '[mask-image:linear-gradient(to_right,transparent_0,transparent_40px,black_64px)]'
    : '[mask-image:linear-gradient(to_right,transparent_0,transparent_40px,black_64px,black_calc(100%-32px),transparent)]';

  return (
    <div className="relative w-full">
      <button
        type="button"
        onClick={onOpenMobileFilters}
        className="md:hidden absolute left-0 top-1/2 -translate-y-1/2 z-10 shrink-0 w-10 h-10 rounded-full border border-zinc-700 bg-black flex items-center justify-center text-white"
        aria-label={t('openFilters')}
      >
        <FilterIcon size={20} />
        {hasUserAppliedFilters && (
          <span className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-1/2 bg-white rounded-full h-2 w-2" aria-hidden />
        )}
      </button>
      <div
        ref={fade.ref}
        onScroll={fade.onScroll}
        className={`flex flex-row items-center gap-3 md:flex-wrap overflow-x-auto md:overflow-visible [&::-webkit-scrollbar]:hidden [scrollbar-width:none] pl-16 md:pl-0 md:[mask-image:none] ${mobileMask}`}
      >
        {orderedChips.map((gear) => (
          <GearChip
            key={gear}
            gear={gear}
            isActive={activeSet.has(gear)}
            isDisabled={isChipDisabled(gear)}
            onClick={() => onToggle(gear)}
          />
        ))}
      </div>
    </div>
  );
};

export default GearChips;
